import { AxiosError } from "axios";
import { verifyBusinessNumber, type ExtraEntityDetail } from "Hooks/Api/apiBusinessNumber";
import { action, observable, runInAction } from "mobx";
import { IModelAttributes, Model } from "Models/Model";
import type { Errors } from "Util/CustomTypes";

export interface IBusinessValidationAttributes extends IModelAttributes {
	abn: string | null;
	acn: string | null;
	abnExtraDetails: ExtraAbnDetail;
	acnExtraDetails: ExtraEntityDetail;
	errors: Errors;

	requiredAbn?: boolean;
	requiredAcn?: boolean;
}

export type ExtraAbnDetail = ExtraEntityDetail & {
	firstName?: string;
	lastName?: string;
	otherGivenName?: string;
}

/**
 * Wrapper class for validating abn and acn information from the apis
 */
export class BusinessValidation
	extends Model
	implements IBusinessValidationAttributes {

	@observable public abn: string | null;

	@observable public acn: string | null;

	@observable public abnFinishedValidating: boolean = true;

	@observable public acnFinishedValidating: boolean = true;

	@observable public errors: Errors = {};

	@observable public abnExtraDetails: ExtraAbnDetail = {}

	@observable public acnExtraDetails: ExtraEntityDetail = {}

	@observable public requiredAbn?: boolean = false;

	@observable public requiredAcn?: boolean = false;

	constructor(attributes?: Partial<IBusinessValidationAttributes>) {
		super(attributes);
	}

	@action
	public assignAttributes(attributes?: Partial<IBusinessValidationAttributes>) {
		super.assignAttributes(attributes);

		if (attributes) {
			if (attributes.abn !== undefined) {
				this.abn = attributes.abn;
			}
			if (attributes.acn !== undefined) {
				this.acn = attributes.acn;
			}
			if (attributes.abnExtraDetails !== undefined) {
				this.abnExtraDetails = attributes.abnExtraDetails;
			}
			if (attributes.acnExtraDetails !== undefined) {
				this.acnExtraDetails = attributes.acnExtraDetails;
			}
			if (attributes.requiredAbn !== undefined) {
				this.requiredAbn = attributes.requiredAbn;
			}
			if (attributes.requiredAcn !== undefined) {
				this.requiredAcn = attributes.requiredAcn;
			}
		}
	}

	/**
	 * 1. Set or clear the error on key propertyName 
	 * 2. Clear the abn/acn details for the matching propertyName if the abn/acn is invalid
	 */
	@action
	async setPropertyError(propertyName: ValidateBusinessNumberPropertyNames, message: string) {

		const fieldName = propertyName === "ACN" || propertyName === "Trustee ACN" ? "acn" : "abn";

		// Clear the abn/acn details if we have an invalid abn/acn
		if ((propertyName === "ACN" || propertyName === "Trustee ACN")) this.acnExtraDetails = {};

		if (propertyName === "ABN") this.abnExtraDetails = {};

		// Set the error
		this.setFieldError(fieldName, true, message);
	}

	// function for running abn validation for this customer entity, and set values
	// for each of the fields that are returned from ABR
	@action
	async runAbnValidation(): Promise<void> {

		runInAction(() => this.abnFinishedValidating = false);

		// Clear all errors before we re-validate and potentially set new errors. However, if there is an EXTERNAL error message, do not clear it.
		if (PredefinedBusinessNumberErrors.includes(this.errors['abn'])) {
			runInAction(() => delete this.errors['abn']);
		}
		try {

			const result = await BusinessValidationUtil.validateBusinessNumber(this.abn, undefined, {
				requiredAbn: this.requiredAbn,
				propertyName: "ABN"
			});

			if (result.error == null) {
				runInAction(() => {
					// Only need to grab the fields we are displaying, everything else is set in the backend
					this.abnExtraDetails = result.abnExtraDetails ?? {};
					this.acnExtraDetails = result.acnExtraDetails ?? {};
					this.acn = result.acnExtraDetails?.identifierNumber ?? null;
				});
			} else {
				// Only set error when there is one, otherwise we delete the CUSTOM ones we set outside.
				this.setPropertyError("ABN", result.error.message);
			}
		} catch (err) {
			const axiosError = err as AxiosError;
			this.setPropertyError("ABN", axiosError.message);
		} finally {
			runInAction(() => this.abnFinishedValidating = true);
		}
	}

	@action
	async runAcnValidation(): Promise<void> {

		runInAction(() => this.acnFinishedValidating = false);
		// Clear all errors before we re-validate and potentially set new errors. However, if there is an EXTERNAL error message, do not clear it.
		if (PredefinedBusinessNumberErrors.includes(this.errors['acn'])) {
			runInAction(() => delete this.errors['acn']);
		}

		try {
			const result = await BusinessValidationUtil.validateBusinessNumber(undefined, this.acn, {
				requiredAcn: this.requiredAcn,
				propertyName: "ACN"
			});

			if (result.error == null) {
				runInAction(() => {
					this.abnExtraDetails = result.abnExtraDetails ?? {};
					this.acnExtraDetails = result.acnExtraDetails ?? {};
					this.abn = result.abnExtraDetails?.identifierNumber ?? null;
				});
			} else {
				// Only set error when there is one, otherwise we delete the CUSTOM ones we set outside.
				this.setPropertyError("ACN", result.error.message);
			}
		} catch (err) {
			const axiosError = err as AxiosError;
			this.setPropertyError("ACN", axiosError.message);
		} finally {
			runInAction(() => this.acnFinishedValidating = true);
		}
	}


}



export type ValidateBusinessNumberPropertyNames = "ACN" | "ABN" | "Trustee ACN";

export const mapToEntityProperties: Record<ValidateBusinessNumberPropertyNames, string> = {
	ABN: "abn",
	ACN: "acn",
	"Trustee ACN": "trusteeACN"
};

export type ValidateBusinessNumberOptions = {
	propertyName: ValidateBusinessNumberPropertyNames
	requiredAbn?: boolean;
	requiredAcn?: boolean;
}

export type ValidateBusinessNumberError = {
	propertyName: ValidateBusinessNumberPropertyNames;
	message: string;
}

export type ValidatedBusinessDetails = {
	abnExtraDetails?: ExtraAbnDetail;
	acnExtraDetails?: ExtraEntityDetail
	error?: ValidateBusinessNumberError;
}


export const BusinessValidationUtil = {

	async validateBusinessNumber(
		abn: string | null | undefined,
		acn: string | null | undefined,
		options: ValidateBusinessNumberOptions): Promise<ValidatedBusinessDetails> {

		const propertyName = options.propertyName;

		// Check either the ACN set or the ABN set. 
		// The related pair value that comes back from the backend is already validated
		if (propertyName === "ACN" || propertyName === "Trustee ACN") {
			const acnRequired = options.requiredAcn ?? false;

			// If acn is not required and its empty, its valid
			if (!acnRequired && (acn == null || acn === "")) return {}

			const isAcnRequiredAndUnset = BusinessNumberGuards.requiredAcnUnset(acn, acnRequired);
			if (isAcnRequiredAndUnset) {
				return {
					error: isAcnRequiredAndUnset
				}
			}


			const invalidAcnLength = BusinessNumberGuards.invalidAcnLength(acn);
			if (invalidAcnLength) {
				return {
					error: invalidAcnLength
				}
			}
		} else {
			const requiredAbn = options.requiredAbn ?? false;

			// If abn is not required and its empty, its valid
			if (!requiredAbn && (abn == null || abn === "")) return {}

			const isAbnRequiredAndUnset = BusinessNumberGuards.requiredAbnUnset(abn, requiredAbn);
			if (isAbnRequiredAndUnset) {
				return {
					error: isAbnRequiredAndUnset
				}
			}
			const invalidAbnLength = BusinessNumberGuards.invalidAbnLength(abn);
			if (invalidAbnLength) {
				return {
					error: invalidAbnLength
				}
			}
		}

		const response = await verifyBusinessNumber(abn, acn);
		// handle the error response from the api
		if (response.error) {
			return {
				error: {
					propertyName: propertyName,
					message: response.error,
				}
			}
		}

		return {
			abnExtraDetails: response.abnExtraDetails,
			acnExtraDetails: response.acnExtraDetails
		}
	},
}

const AbnEmpty = "ABN cannot be empty";
const AcnEmpty = "ACN cannot be empty";
const AbnBadLength = "The ABN must contain 11 digits";
const AcnBadLength = "The ACN must contain 9 digits";

const PredefinedBusinessNumberErrors = [AbnEmpty, AcnEmpty, AbnBadLength, AcnBadLength];

export const BusinessNumberGuards = {
	requiredAbnUnset(abn: string | null | undefined, isRequired: boolean): ValidateBusinessNumberError | null {
		if (isRequired && (!abn || abn.length === 0)) {
			return {
				propertyName: "ABN",
				message: AbnEmpty,
			};
		}
		return null;
	},

	requiredAcnUnset(acn: string | null | undefined, isRequired: boolean): ValidateBusinessNumberError | null {
		if (isRequired && (!acn || acn.length === 0)) {
			return {
				propertyName: "ACN",
				message: AcnEmpty,
			};
		}
		return null;
	},

	invalidAbnLength(abn: string | null | undefined): ValidateBusinessNumberError | null {
		if (abn && abn.length !== 11) {
			return {
				propertyName: "ABN",
				message: AbnBadLength,
			}
		}

		return null;
	},

	invalidAcnLength(acn: string | null | undefined): ValidateBusinessNumberError | null {
		if (acn && acn.length !== 9) {
			return {
				propertyName: "ACN",
				message: AcnBadLength,
			};
		}
		return null;
	},
};